import React from 'react';
import { motion } from 'framer-motion';

/**
 * Bouton réutilisable avec variantes premium
 * variant: 'gold' | 'green' | 'outline' | 'ghost' | 'danger' | 'dark' | 'secondary'
 * size: 'sm' | 'md' | 'lg' | 'xl'
 */
function Button({
  children,
  variant = 'gold',
  size = 'md',
  fullWidth = false,
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  className = '',
  premium = false,
  ...props
}) {
  const baseClasses = 'font-bold uppercase tracking-wider rounded-2xl transition-all duration-150 flex items-center justify-center gap-2 select-none relative overflow-hidden';

  const variantClasses = {
    gold: premium
      ? 'bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 text-black hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed'
      : 'bg-qfl-gold text-black hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed',
    green: premium
      ? 'bg-gradient-to-br from-green-400 via-green-500 to-green-600 text-white hover:shadow-lg disabled:opacity-50'
      : 'bg-qfl-green text-white hover:bg-green-400 disabled:opacity-50 disabled:cursor-not-allowed',
    outline: premium
      ? 'border-2 border-qfl-gold text-qfl-gold hover:bg-qfl-gold/10 hover:shadow-[0_0_20px_rgba(240,180,41,0.4)] disabled:opacity-50'
      : 'border-2 border-qfl-gold text-qfl-gold hover:bg-qfl-gold hover:text-black disabled:opacity-50',
    ghost: 'text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-50',
    danger: 'bg-qfl-red text-white hover:bg-red-400 disabled:opacity-50',
    dark: 'bg-white/10 text-white border border-white/20 hover:bg-white/20 disabled:opacity-50',
    secondary: 'bg-white/10 text-white/80 border border-white/20 hover:bg-white/20 disabled:opacity-50',
  };

  const sizeClasses = {
    sm: 'py-2 px-4 text-sm',
    md: 'py-3 px-6 text-base',
    lg: 'py-4 px-8 text-xl',
    xl: 'py-5 px-10 text-2xl',
  };

  const shadowClasses = {
    gold: premium
      ? 'shadow-[0_6px_30px_rgba(240,180,41,0.6)]'
      : 'shadow-[0_4px_20px_rgba(240,180,41,0.4)]',
    green: premium
      ? 'shadow-[0_6px_25px_rgba(34,197,94,0.5)]'
      : 'shadow-[0_4px_15px_rgba(34,197,94,0.3)]',
    outline: '',
    ghost: '',
    danger: 'shadow-[0_4px_15px_rgba(239,68,68,0.3)]',
    dark: '',
    secondary: '',
  };

  // Shimmer effect pour premium buttons
  const shimmerStyle = premium
    ? {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%)',
        backgroundSize: '200% 100%',
        opacity: 0,
      }
    : null;

  return (
    <motion.button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      whileHover={disabled || loading ? {} : premium ? { scale: 1.05, y: -2 } : { scale: 1.02 }}
      whileTap={disabled || loading ? {} : { scale: 0.96 }}
      className={`
        ${baseClasses}
        ${variantClasses[variant] || variantClasses.gold}
        ${sizeClasses[size]}
        ${shadowClasses[variant] || ''}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      style={{ fontFamily: "'Rajdhani', sans-serif" }}
      {...props}
    >
      {premium && <motion.div style={shimmerStyle} />}

      {loading ? (
        <>
          <span className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
          <span>Chargement...</span>
        </>
      ) : (
        children
      )}
    </motion.button>
  );
}

export default Button;
